import { Button, Classes, Dialog, FormGroup, Intent, Spinner } from '@blueprintjs/core'
import { lang, toast } from 'botpress/shared'
import { UserProfile } from 'common/typings'
import React, { FC, useState } from 'react'
import api from '~/app/api'
import { PasswordStrengthMeter } from '~/auth/PasswordStrengthMeter/PasswordStrengthMeter'
import packageJson from '../../../../package.json'
import { encryptPayload } from '../../aes-encryption'
import { jwtKeyStore, formDataStore } from '../../token-store'
import './modern-settings.css'

interface Props {
  isOpen: boolean
  profile: UserProfile
  toggle: () => void
}

const UpdatePassword: FC<Props> = props => {
  const [password, setPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const token = jwtKeyStore.get() || '' || ''

  const CURRENT_VERSION = packageJson.version
  const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'

  const submit = async event => {
    event.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.failure('New passwords do not match')
      return
    }

    setIsLoading(true)

    const res = await dbCall({
      currentPassword: password,
      newPassword
    })
    setIsLoading(false)

    if (!res.success) {
      toast.failure(res.msg)
      return
    }

    props.toggle()
    toast.success(res.msg)
  }

  const dbCall = async (data) => {
    try {
      const result = await fetch(`${API_URL}/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({ payload: encryptPayload({ data }) }),
      })

      return result.json()
    } catch (error) {
      return { success: false, msg: 'Error connecting to database' }
    }
  }

  const clear = () => {
    setPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setShowPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  const renderShowHideButton = (visible: boolean, setVisible: (val: boolean) => void) => (
    <Button
      icon={visible ? 'eye-open' : 'eye-off'}
      minimal={true}
      onClick={() => setVisible(!visible)}
      style={{ color: '#64748b', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
    />
  )

  return (
    <>
      <Dialog
        title={lang.tr('admin.changeYourPassword')}
        icon="key"
        isOpen={props.isOpen}
        onClose={props.toggle}
        onClosed={clear}
        transitionDuration={0}
        canOutsideClickClose={false}
        className="modern-dialog"
        style={{ width: 500 }}
      >
        <form onSubmit={submit}>
          <div className={Classes.DIALOG_BODY}>
            <FormGroup label={lang.tr('admin.currentPassword')} className="modern-form-group">
              <div style={{ position: 'relative' }}>
                <input
                  id="input-password"
                  type={showPassword ? 'text' : 'password'}
                  className="modern-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  tabIndex={1}
                  autoFocus={true}
                  style={{ paddingRight: '40px' }}
                />
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                  {renderShowHideButton(showPassword, setShowPassword)}
                </div>
              </div>
            </FormGroup>

            <FormGroup label={lang.tr('admin.newPassword')} className="modern-form-group">
              <div style={{ position: 'relative' }}>
                <input
                  id="input-newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  className="modern-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  tabIndex={2}
                  style={{ paddingRight: '40px' }}
                />
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                  {renderShowHideButton(showNewPassword, setShowNewPassword)}
                </div>
              </div>
            </FormGroup>

            <FormGroup label={lang.tr('admin.confirmPassword')} className="modern-form-group">
              <div style={{ position: 'relative' }}>
                <input
                  id="input-confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="modern-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  tabIndex={3}
                  style={{ paddingRight: '40px' }}
                />
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                  {renderShowHideButton(showConfirmPassword, setShowConfirmPassword)}
                </div>
              </div>
            </FormGroup>
            <div style={{ marginTop: '16px' }}>
              <PasswordStrengthMeter pwdCandidate={newPassword} />
            </div>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button
                id="btn-submit-update-password"
                type="submit"
                text={lang.tr('save')}
                tabIndex={4}
                className="modern-btn-primary"
                disabled={!password || !newPassword || newPassword !== confirmPassword}
              />
            </div>
          </div>
        </form>
        {isLoading && (
          <div className="modern-loader">
            <Spinner size={50} />
            <div className="modern-loader-text">Updating your password...</div>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default UpdatePassword
